import { useMemo } from 'react'
import { Box, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'

import { claimGoalSchema } from '@/modules/actionPlan/pages/components/ClaimGoalModal/ClaimGoalModal.schema'
import type {
  ClaimGoalModalProps,
  ClaimGoalSchema,
} from '@/modules/actionPlan/pages/components/ClaimGoalModal/ClaimGoalModal.types'

import { Button } from '@/components/Button/Button'
import { Modal } from '@/components/Modal/Modal'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { Switch } from '@/components/Switch/Switch'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import { INVALIDATE_KEYS } from '@/constants/invalidateGoalQueryKeys.constants'

import { useGoalsServiceGoalsControllerClaimAmount } from '@/__generated__/queries'

import { useGoalSteps } from '@/hooks/useGoalSteps.hook'

import classes from './ClaimGoalModal.module.css'

export function ClaimGoalModal({ goal, onClose, opened }: ClaimGoalModalProps) {
  const isMutating = useIsMutating({
    exact: true,
  })

  const queryClient = useQueryClient()

  const { data: steps } = useGoalSteps(goal.id)

  const stepOptions = useMemo(
    () =>
      (steps ?? []).map(step => ({
        label: step.title,
        value: String(step.id),
      })),
    [steps],
  )

  const form = useForm<ClaimGoalSchema>({
    initialValues: {
      amount: goal.monthlyReminderAmount ?? 0,
      goalStepId: null,
      isNegative: false,
      note: '',
    },
    validate: zodResolver(claimGoalSchema),
  })

  const claimGoalMutation = useGoalsServiceGoalsControllerClaimAmount({
    onError: (error: unknown) => {
      const message =
        error &&
        typeof error === 'object' &&
        'body' in error &&
        error.body &&
        typeof error.body === 'object' &&
        'message' in error.body
          ? String(error.body.message)
          : 'Unable to log contribution. The goal may not be active.'

      notifications.show({
        color: 'red',
        message,
        title: 'Error',
      })
    },
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Money claimed successfully',
        title: 'Money claimed',
      })

      INVALIDATE_KEYS.forEach(key => {
        queryClient.invalidateQueries({
          queryKey: [key],
        })
      })

      onClose()
    },
  })

  const onSubmit = (values: ClaimGoalSchema) => {
    const amount = values.amount * (values.isNegative ? -1 : 1)

    claimGoalMutation.mutate({
      goalId: goal.id,
      requestBody: {
        amount,
        goalStepId: values.goalStepId ? Number(values.goalStepId) : undefined,
        note: values.note || undefined,
      },
    })
  }

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  const handleSwitchChange = (event: {
    target: { checked: boolean | ((prevValue: boolean) => boolean) }
  }) => {
    form.setFieldValue('isNegative', event.target.checked)
  }

  return (
    <Modal
      centered
      classNames={classes}
      onClose={onClose}
      opened={opened}
      withCloseButton={false}
    >
      <Flex align="center" direction="column">
        <Title mb={8} variant="h1">
          Log Your Contribution
        </Title>

        <Text>
          Add the amount you&apos;ve saved toward this goal. This will be
          reflected in the progress bar.
        </Text>
      </Flex>

      <Box component="form" mt={32} noValidate onSubmit={handleSubmit}>
        <NumberInput
          hideControls
          label="Amount"
          placeholder="Enter amount"
          {...form.getInputProps('amount')}
        />

        {stepOptions.length > 0 && (
          <SelectInput
            clearable
            data={stepOptions}
            label="Goal step"
            placeholder="Select a step"
            searchable
            {...form.getInputProps('goalStepId')}
          />
        )}

        <TextInput
          label="Note"
          placeholder="Add a note (optional)"
          {...form.getInputProps('note')}
        />

        <Switch
          checked={form.values.isNegative ?? false}
          label="Enable if you're removing from your progress"
          onChange={handleSwitchChange}
        />

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button loading={!!isMutating} miw="48%" type="submit">
            Log
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
}
